import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { fmtDate, fmtTime, combineDateTime } from "./date";
import { toEnglishDigits } from "./toEnglishDigits";
import { formatPhonePlain } from "./phoneFormat";
import { getBookingAirline, getBookingFlightNumber } from "./airline";

// Localize booking statuses + assign light background colors matching UI chips.
export const STATUS_I18N = {
  pending: { label: "قيد المراجعة", bg: "FEF3C7", fg: "78350F" },
  confirmed: { label: "مؤكد", bg: "DBEAFE", fg: "1E3A8A" },
  in_progress: { label: "قيد التنفيذ", bg: "D1FAE5", fg: "065F46" },
  completed: { label: "منتهي", bg: "A7F3D0", fg: "064E3B" },
  rejected: { label: "ملغي", bg: "FEE2E2", fg: "7F1D1D" },
  cancelled: { label: "ملغي", bg: "FEE2E2", fg: "7F1D1D" },
};

const NAVY = "0B1E4F";
const GOLD = "D4AF37";

const border = {
  top: { style: "thin", color: { rgb: "334155" } },
  bottom: { style: "thin", color: { rgb: "334155" } },
  left: { style: "thin", color: { rgb: "334155" } },
  right: { style: "thin", color: { rgb: "334155" } },
};

function headerCell(v) {
  return {
    v,
    t: "s",
    s: {
      font: { bold: true, color: { rgb: GOLD }, sz: 12, name: "Cairo" },
      fill: { fgColor: { rgb: NAVY } },
      alignment: { horizontal: "center", vertical: "center", readingOrder: 2 },
      border,
    },
  };
}

function textCell(v, extra = {}) {
  return {
    v: v ?? "",
    t: "s",
    s: {
      font: { name: "Cairo", sz: 11 },
      alignment: { horizontal: "center", vertical: "center", readingOrder: 2, wrapText: true },
      border,
      ...extra,
    },
  };
}

function numCell(v) {
  const num = Number(v || 0);
  return {
    v: num,
    t: "n",
    z: "#,##0",
    s: {
      font: { name: "Cairo", sz: 11 },
      alignment: { horizontal: "center", vertical: "center" },
      border,
    },
  };
}

function statusCell(status) {
  const info = STATUS_I18N[status] || { label: status || "—", bg: "F3F4F6", fg: "111827" };
  return {
    v: info.label,
    t: "s",
    s: {
      font: { name: "Cairo", sz: 11, bold: true, color: { rgb: info.fg } },
      fill: { fgColor: { rgb: info.bg } },
      alignment: { horizontal: "center", vertical: "center", readingOrder: 2 },
      border,
    },
  };
}

export function exportBookingsToExcel(bookings, opts = {}) {
  const filename = opts.filename || `bookings-${new Date().toISOString().slice(0, 10)}.xlsx`;

  const headers = [
    "رقم الحجز",
    "التاريخ",
    "الوقت",
    "اسم العميل",
    "الهاتف",
    "السائق",
    "المسار",
    "شركة الطيران",
    "رقم الرحلة",
    "نوع السيارة",
    "المبلغ (ر.س)",
    "أجرة السائق",
    "صافي",
    "الحالة",
  ];

  const rows = bookings.map((b) => {
    const iso = b.pickup_time || combineDateTime(b.trip_date, b.trip_time);
    const route = `${b.pickup_location || "—"} → ${b.destination || b.dropoff_location || "—"}`;
    const price = Number(b.price || b.total_price || 0);
    const wage = Number(b.driver_wage || 0);
    const net = price - wage;
    return [
      textCell(toEnglishDigits(b.booking_number || "—")),
      textCell(iso ? fmtDate(iso) : "—"),
      textCell(iso ? fmtTime(iso) : "—"),
      textCell(b.client_name || "—"),
      textCell(b.client_phone ? formatPhonePlain(b.client_phone) : "—"),
      textCell(b.driver_name || b.external_driver_name || "—"),
      textCell(route),
      textCell(getBookingAirline(b) || "—"),
      textCell(getBookingFlightNumber(b) || "—"),
      textCell(b.car_type_name || b.car_type || "—"),
      numCell(price),
      numCell(wage),
      numCell(net),
      statusCell(b.status),
    ];
  });

  const aoa = [headers.map(headerCell), ...rows];
  const ws = XLSX.utils.aoa_to_sheet([[]]);
  XLSX.utils.sheet_add_aoa(ws, aoa, { origin: "A1" });

  ws["!views"] = [{ rightToLeft: true }];
  ws["!cols"] = [
    { wch: 16 }, // رقم الحجز
    { wch: 12 }, // التاريخ
    { wch: 8 },  // الوقت
    { wch: 22 }, // اسم العميل
    { wch: 15 }, // الهاتف
    { wch: 20 }, // السائق
    { wch: 40 }, // المسار
    { wch: 16 }, // شركة الطيران
    { wch: 14 }, // رقم الرحلة
    { wch: 16 }, // نوع السيارة
    { wch: 13 }, // المبلغ
    { wch: 13 }, // أجرة السائق
    { wch: 13 }, // صافي
    { wch: 14 }, // الحالة
  ];
  ws["!rows"] = [{ hpt: 28 }, ...rows.map(() => ({ hpt: 22 }))];

  // Freeze header row
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  ws["!autofilter"] = { ref: `A1:N${rows.length + 1}` };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "الحجوزات");

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
  saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
}
